import { PlusIcon } from "@xola/icons";
import { PopoverList, Spinner, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { getAddOns, isAddonField, Loader, TEMP_ID_PREFIX } from "common";
import { orderBy, uniqueId } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { useMutation } from "react-query";
import type { Instance } from "tippy.js";
import {
    AddOn,
    CatalogItem,
    CatalogItemObject,
    CatalogType,
    DisplayStepOption,
    Experience,
    Field,
    FieldPayload,
    FieldType,
    FieldVisibility,
    Form,
    HeightUnit,
    LineItemType,
    Product,
    WeightUnit,
} from "types";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { AddonQuestionDeletionModal } from "../GlobalSettings/AddOns/AddonQuestionDeletionModal";
import { AddOnEditorFormState } from "../GlobalSettings/AddOns/AddOnTypes";
import { EditAddOnModal } from "../GlobalSettings/AddOns/EditAddOnModal";
import { linkGlobalAddOnToExperience } from "../GlobalSettings/AddOns/ExperienceCreateAddOnPopover";
import { QuestionField } from "./QuestionField";
import { QuestionFormModal } from "./QuestionFormModal";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { PromptModal } from "@/components/Common/PromptModal";
import { useCatalogItems } from "@/hooks/resources/useCatalogItems";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { isMatchingGuestCount } from "@/models/CatalogItem";
import { defaultField } from "@/models/Form/Field";
import {
    CustomQuestions,
    getModalType,
    PredefinedQuestions,
    QuestionnaireModalType,
} from "@/models/Form/QuestionnaireModalType";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";
import { getSeller } from "@/utils/getSeller";
import { reorder } from "@/utils/reorder";

export enum AddonQuestionAction {
    REMOVE_FROM_PRODUCT = "removeFromProduct",
    CONVERT_TO_ORGANIZER = "convertToOrganizer",
}

export interface AddonQuestionDeletion {
    fieldId: string;
    catalogItemId: string;
    action: AddonQuestionAction;
}

// The API rejects asking each attendee for an add-on whose quantity comes from the guest count.
export const GUEST_COUNT_MATCH_TOOLTIP =
    "To ask each attendee, turn off the ‘Automatically match add-on quantity’ setting for this add-on.";

const getAddOnItemClassName = (addOn: CatalogItem, className: string) => {
    // `!` beats the item's own cursor-pointer
    return clsx(className, isMatchingGuestCount(addOn) && "!cursor-not-allowed text-gray hover:bg-white");
};

interface GuestCountMatchTooltipProps {
    addOn: CatalogItem;
    children: React.ReactNode;
}

const GuestCountMatchTooltip = ({ addOn, children }: GuestCountMatchTooltipProps) => (
    <Tooltip
        className="inline-flex items-center space-x-2.5"
        content={GUEST_COUNT_MATCH_TOOLTIP}
        disabled={!isMatchingGuestCount(addOn)}
    >
        {children}
    </Tooltip>
);

interface QuestionsProps {
    form: Form | null;
    onFormChange: (form: Form) => void;
    title?: string;
    isAttendeeUnique?: boolean;
    isMembershipProduct?: boolean;
    product?: Product;
    onAddonDeletionsChange?: (deletions: AddonQuestionDeletion[]) => void;
}

export const Questions = ({
    form,
    onFormChange,
    isMembershipProduct,
    isAttendeeUnique,
    product,
    onAddonDeletionsChange,
}: QuestionsProps) => {
    const questionsPopover = useModal();
    const promptModal = useModal();
    const createAddOnModal = useModal();
    const addOnsPopover = useModal();

    const [selectedAddon, setSelectedAddon] = useState<string>();
    const [offset, setOffset] = useState([0, 300]);
    const seller = getSeller();
    const invalidate = useInvalidate();

    const [currentModalType, setCurrentModalType] = useState<QuestionnaireModalType | null>(null);
    const [currentField, setCurrentField] = useState<FieldPayload | null>(null);

    const addonDeletionModal = useModal();
    const [pendingAddonField, setPendingAddonField] = useState<Field | null>(null);
    const addonDeletionsRef = useRef<AddonQuestionDeletion[]>([]);

    const [editAddOnModalOpen, setEditAddOnModalOpen] = useState(false);
    const [editingAddon, setEditingAddon] = useState<AddOn | null>(null);

    const { data: globalAddOns, isLoading: isGlobalAddonsLoading } = useCatalogItems({
        "unitType[in]": [LineItemType.ADDON, LineItemType.PICKUP].join(","),
        object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
        "type[nin]": CatalogType.CHOICES_ITEM,
    });

    const { data: productAddOns, isLoading: isProductAddonsLoading } = useCatalogItems({
        product: product?.id,
        "unitType[in]": [LineItemType.ADDON, LineItemType.PICKUP].join(","),
        object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
        "type[nin]": CatalogType.CHOICES_ITEM,
    });

    const { mutateAsync: linkAddOnToExperience, isLoading: isLinkingAddOn } = useMutation(async (addOn: CatalogItem) =>
        linkGlobalAddOnToExperience(addOn, product as Experience, seller.id),
    );

    const questionnaireAddOnIds = useMemo(() => {
        const questionnaireAddons = form?.fields?.filter((field) => isAddonField(field.type) && field.catalogItem);

        return new Set(questionnaireAddons?.map((addon) => addon.catalogItem?.parent?.id).filter(Boolean) ?? []);
    }, [form?.fields]);

    const remainingAddOns = useMemo(() => {
        if (!product) {
            return [];
        }
        const experienceAddOns = getAddOns(product as Experience);
        const experienceAddOnIds = new Set(experienceAddOns.map((addOn) => addOn.parent?.id));
        return globalAddOns.filter(
            (addOn) =>
                !!addOn.type &&
                addOn.type !== CatalogType.CHOICES_ITEM &&
                !experienceAddOnIds.has(addOn.id) &&
                !questionnaireAddOnIds.has(addOn.id),
        );
    }, [product, questionnaireAddOnIds, globalAddOns]);

    const filteredCustomQuestions = CustomQuestions.filter(
        (question) => question.type !== QuestionnaireModalType.PICKUP,
    );

    const filteredPredefinedQuestions = useMemo(() => {
        if (isMembershipProduct) {
            return PredefinedQuestions.filter((question) => question.type !== QuestionnaireModalType.EMAIL);
        }
        return PredefinedQuestions;
    }, [isMembershipProduct]);

    if (isGlobalAddonsLoading || isProductAddonsLoading) {
        return <Loader />;
    }

    const filteredGlobalAddOns = globalAddOns.filter((addOn) => addOn.type && addOn.type !== CatalogType.CHOICES_ITEM);
    // Filter out addons that are already present in form fields.
    const filteredProductAddOns = productAddOns.filter(
        (addOn) =>
            addOn.type &&
            addOn.type !== CatalogType.CHOICES_ITEM &&
            !form?.fields.some((field) => isAddonField(field.type) && field.catalogItem?.id === addOn.id),
    );

    if (!form) {
        return null;
    }

    const showQuestionFormModal = (modalType: QuestionnaireModalType, field: FieldPayload) => {
        setCurrentModalType(modalType);
        setCurrentField(field);
        questionsPopover.hide();
    };

    const closeQuestionFormModal = () => {
        setCurrentModalType(null);
        setCurrentField(null);
    };

    const handleAddedQuestion = (field: FieldPayload) => {
        if (form) {
            let fields = [...form.fields];
            if (field.id) {
                fields = fields.map((innerField) => {
                    return innerField.id === field.id ? (field as Field) : innerField;
                });
            } else {
                fields.push({ ...field, id: uniqueId(TEMP_ID_PREFIX) } as Field);
            }

            onFormChange({ ...form, fields });

            closeQuestionFormModal();
        }
    };

    const handleDelete = (field: Field) => {
        if (isAddonField(field.type) && field.catalogItem) {
            setPendingAddonField(field);
            addonDeletionModal.show();
            return;
        }

        onFormChange({ ...form, fields: form.fields.filter((formField) => formField.id !== field.id) });
    };

    const handleAddonDeletion = (action: AddonQuestionAction) => {
        if (!pendingAddonField?.catalogItem?.id) {
            return;
        }

        const deletion: AddonQuestionDeletion = {
            fieldId: pendingAddonField.id,
            catalogItemId: pendingAddonField.catalogItem.id,
            action,
        };

        addonDeletionsRef.current = [...addonDeletionsRef.current, deletion];
        onAddonDeletionsChange?.(addonDeletionsRef.current);

        onFormChange({
            ...form,
            fields: form.fields.filter((field) => field.id !== pendingAddonField.id),
        });

        setPendingAddonField(null);
    };

    const handleRemoveAddOnFromProduct = () => handleAddonDeletion(AddonQuestionAction.REMOVE_FROM_PRODUCT);

    const handleConvertToOrganizerAddon = () => handleAddonDeletion(AddonQuestionAction.CONVERT_TO_ORGANIZER);

    const handleEdit = (field: Field) => {
        if (isAddonField(field.type) && field.catalogItem) {
            const catalogItemId = field.catalogItem.id;
            const productAddon = productAddOns?.find((addon) => addon.id === catalogItemId);

            if (productAddon) {
                setEditingAddon(productAddon as AddOn);
                setEditAddOnModalOpen(true);
                return;
            }
        }

        showQuestionFormModal(getModalType(field), field);
    };

    const fields = orderBy(form.fields, ["seq"]);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        if (form) {
            onFormChange({
                ...form,
                fields: reorder(fields, result.source.index, result.destination.index).map((field, index) => ({
                    ...field,
                    seq: (index + 1) * 10,
                })),
            });
        }
    };

    const handleAddNewQuestion = (modalType: QuestionnaireModalType, catalogItem?: CatalogItem) => {
        showQuestionFormModal(
            modalType,
            getInitialField(modalType, form.fields.length, catalogItem, isMembershipProduct),
        );
    };

    const handleProductAddOnItemClick = (catalogItem: CatalogItem) => {
        if (isMatchingGuestCount(catalogItem)) {
            return;
        }

        handleAddProductAddOn(catalogItem);
    };

    const handleLinkGlobalAddOnItemClick = (catalogItem: CatalogItem) => {
        if (isMatchingGuestCount(catalogItem)) {
            return;
        }

        setSelectedAddon(catalogItem.id);
        questionsPopover.hide();
        addOnsPopover.hide();
        promptModal.show();
    };

    const handleLinkGlobalAddOn = async (catalogItem: CatalogItem, formValues?: AddOnEditorFormState) => {
        const { data: productCatalogItem } = await linkAddOnToExperience(catalogItem);
        handleAddProductAddOn(productCatalogItem, formValues);

        invalidate("catalogItems");
        if (product) {
            invalidate("experiences", product.id);
        }
        invalidate("purchaseRuleLinks");

        addOnsPopover.hide();
        questionsPopover.hide();
    };

    const handleAddProductAddOn = (productCatalogItem: CatalogItem, formValues?: AddOnEditorFormState) => {
        const newFieldPayload = getInitialField(
            QuestionnaireModalType.ADDON,
            form.fields.length,
            productCatalogItem,
            isMembershipProduct,
        );
        newFieldPayload.required = productCatalogItem?.required ?? formValues?.required ?? false;
        newFieldPayload.roster = formValues?.roster ?? false;
        newFieldPayload.addToSavedViews = formValues?.addToSavedViews ?? false;
        handleAddedQuestion(newFieldPayload);

        invalidate("catalogItems");
        if (product) {
            invalidate("experiences", product.id);
        }
        invalidate("purchaseRuleLinks");

        addOnsPopover.hide();
        questionsPopover.hide();
    };

    const handleCreateGlobalAddOn = () => {
        createAddOnModal.show();
        addOnsPopover.hide();
        questionsPopover.hide();
    };
    const requireGuestIdentification = product ? (product as Experience).requireGuestIdentification : false;

    const showAddons = !!product && hasX2CheckoutWithFullProbability() && requireGuestIdentification;

    return (
        <div className="space-y-2">
            <div data-testid="questions" className="flex justify-end">
                <PopoverList
                    visible={questionsPopover.isVisible}
                    className="max-h-72 !w-72 overflow-y-auto"
                    onClickOutside={questionsPopover.toggle}
                    // This is for positioning the child popover, here we have two popovers, one for the main list and one for the add-on list
                    onShown={(instance: Instance) => {
                        const placement = instance?.popperInstance?.state?.placement;
                        if (placement === "bottom") {
                            setOffset([0, 300]);
                        }
                        if (placement === "top") {
                            setOffset([0, -50]);
                        }
                    }}
                >
                    <SecondaryButton size="small" data-testid="questions-add-new" onClick={questionsPopover.toggle}>
                        <PlusIcon />
                        Add New Question
                    </SecondaryButton>

                    <PopoverList.Item disabled className="bg-gray-lighter" name="list">
                        <span>Predefined Questions</span>
                    </PopoverList.Item>

                    {filteredPredefinedQuestions.map(({ icon: Icon, name, type }) => (
                        <PopoverList.Item key={type} name={type} onClickItem={() => handleAddNewQuestion(type)}>
                            <Icon />
                            <span>{name}</span>
                        </PopoverList.Item>
                    ))}

                    <PopoverList.Item disabled className="bg-gray-lighter" name="list">
                        <span>Custom Questions</span>
                    </PopoverList.Item>

                    {filteredCustomQuestions.map(({ icon: Icon, name, type }) => (
                        <PopoverList.Item key={type} name={type} onClickItem={() => handleAddNewQuestion(type)}>
                            <Icon />
                            <span>{name}</span>
                        </PopoverList.Item>
                    ))}

                    {showAddons && filteredProductAddOns.length > 0 && (
                        <PopoverList.Item className=" bg-gray-lighter" name="list">
                            <span>Attendee Specific Add-ons</span>
                        </PopoverList.Item>
                    )}

                    {showAddons &&
                        filteredProductAddOns.map((addOn) => (
                            <PopoverList.Item
                                key={addOn.id}
                                className={getAddOnItemClassName(addOn, "flex items-center")}
                                onClickItem={() => handleProductAddOnItemClick(addOn)}
                            >
                                <GuestCountMatchTooltip addOn={addOn}>
                                    <CatalogItemImage catalogItem={addOn} size="tiny" />
                                    {addOn.name}
                                </GuestCountMatchTooltip>
                            </PopoverList.Item>
                        ))}

                    {showAddons && filteredGlobalAddOns.length > 0 && (
                        <PopoverList.Item
                            className={clsx(addOnsPopover.isVisible ? "bg-gray-lighter" : "bg-white")}
                            onClickItem={addOnsPopover.show}
                        >
                            <SecondaryButton icon={<PlusIcon />} className="w-full">
                                Add-on
                            </SecondaryButton>
                        </PopoverList.Item>
                    )}
                    {addOnsPopover.isVisible && (
                        <PopoverList
                            zIndex={100}
                            visible={addOnsPopover.isVisible}
                            offset={offset}
                            arrow={false}
                            className="max-h-72 !w-72 overflow-y-auto"
                            onClickOutside={addOnsPopover.hide}
                        >
                            {remainingAddOns.map((addOn) => (
                                <PopoverList.Item
                                    key={addOn.id}
                                    name={addOn.name}
                                    className={getAddOnItemClassName(addOn, "flex items-center justify-between")}
                                    onClickItem={() => handleLinkGlobalAddOnItemClick(addOn)}
                                >
                                    <GuestCountMatchTooltip addOn={addOn}>{addOn.name}</GuestCountMatchTooltip>
                                    {isLinkingAddOn && selectedAddon === addOn.id && <Spinner size="tiny" />}
                                </PopoverList.Item>
                            ))}
                            <PopoverList.Item
                                name="newAddOn"
                                className="bottom-0 bg-white"
                                onClickItem={handleCreateGlobalAddOn}
                            >
                                <SecondaryButton icon={<PlusIcon />} className="w-full">
                                    New Add-on
                                </SecondaryButton>
                            </PopoverList.Item>
                        </PopoverList>
                    )}
                </PopoverList>
            </div>

            <PromptModal
                isOpen={promptModal.isVisible}
                title="Link Add-on to this Experience?"
                onConfirm={() => {
                    if (selectedAddon) {
                        handleLinkGlobalAddOn({ id: selectedAddon } as CatalogItem);
                    }
                    promptModal.hide();
                }}
                onClose={promptModal.hide}
            >
                <div>
                    This add-on isn&apos;t currently linked to{" "}
                    <b>
                        <var data-var="name">{product?.name}</var>
                    </b>
                    . To use it in this questionnaire, you&apos;ll need to link it first. Would you like us to do that
                    for you now?
                </div>
            </PromptModal>
            <EditAddOnModal
                isQuestionnaire
                isOpen={createAddOnModal.isVisible}
                afterCreate={async (addOn, formValues) => handleLinkGlobalAddOn(addOn, formValues)}
                currentPageExperience={product as Experience}
                onClose={createAddOnModal.hide}
            />

            {currentField && (
                <QuestionFormModal
                    isOpen={!!currentModalType}
                    modalType={currentModalType!}
                    field={currentField}
                    isAttendeeUnique={isAttendeeUnique}
                    isMembershipProduct={isMembershipProduct}
                    experience={product as Experience}
                    onClose={closeQuestionFormModal}
                    onSubmit={handleAddedQuestion}
                />
            )}

            <AddonQuestionDeletionModal
                isOpen={addonDeletionModal.isVisible}
                onClose={() => {
                    addonDeletionModal.hide();
                    setPendingAddonField(null);
                }}
                onRemoveFromProduct={handleRemoveAddOnFromProduct}
                onConvertToOrganizerAddon={handleConvertToOrganizerAddon}
            />

            {editingAddon && (
                <EditAddOnModal
                    isQuestionnaire
                    isOpen={editAddOnModalOpen}
                    addOn={editingAddon}
                    experience={product as Experience}
                    onClose={() => {
                        setEditAddOnModalOpen(false);
                        setEditingAddon(null);
                    }}
                />
            )}

            <div className="flex-1 flex-col justify-between space-y-4 py-2">
                {(!product || fields.length > 0) && <QuestionField field={defaultField} editable={false} />}

                <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
                    <Droppable droppableId="questionList">
                        {(provided: DroppableProvided) => (
                            <div ref={provided.innerRef} className="space-y-4" {...provided.droppableProps}>
                                {fields.map((field, index) => {
                                    const isEmailField = field.type === FieldType.EMAIL;
                                    const isDisabledField = isMembershipProduct && isEmailField;

                                    return (
                                        <Draggable
                                            key={field.id}
                                            draggableId={field.id}
                                            index={index}
                                            isDragDisabled={isDisabledField}
                                        >
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <QuestionField
                                                        isMembership={isMembershipProduct}
                                                        field={field}
                                                        experience={product as Experience}
                                                        draggableProvided={isDisabledField ? undefined : provided}
                                                        editable={!isDisabledField}
                                                        onDelete={handleDelete}
                                                        onEdit={handleEdit}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

const getInitialField = (
    modalType: QuestionnaireModalType,
    fieldsNumber: number,
    catalogItem?: CatalogItem,
    isMembershipProduct?: boolean,
): FieldPayload => {
    const predefinedInfo = PredefinedQuestions.find((info) => info.type === modalType);
    const info = predefinedInfo ?? CustomQuestions.find((info) => info.type === modalType);

    const field: FieldPayload = {
        label: predefinedInfo?.name ?? catalogItem?.name ?? "",
        organizerOnly: false,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: info?.fieldType ?? FieldType.TEXT,
        readonly: !!predefinedInfo,
        seq: 10 * (fieldsNumber + 1),
        visibility: FieldVisibility.ALL, // Adults, Minors, All (only used for specific fields)
    };

    // For memberships, all questions must be asked before payment
    if (isMembershipProduct) {
        field.displayStep = DisplayStepOption.BEFORE_PAYMENT;
    }

    if (modalType === QuestionnaireModalType.WEIGHT) {
        field.unit = WeightUnit.LBS;
        field.summation = false;
    } else if (modalType === QuestionnaireModalType.HEIGHT) {
        field.unit = HeightUnit.FEET;
    }

    if (modalType === QuestionnaireModalType.BIRTH_DATE) {
        field.age = 21;
        field.comparator = "older";
        field.verifyAge = true;
    }

    if (modalType === QuestionnaireModalType.MARKETING_CONSENT) {
        field.optInByDefault = true;
        field.displayStep = DisplayStepOption.BEFORE_PAYMENT;
    }

    if (modalType === QuestionnaireModalType.ADDON && catalogItem) {
        field.type = FieldType.ADDON;
        field.catalogItem = catalogItem;
        // Addon fields must be displayed before payment to ensure users can select and pay for addon during checkout.
        field.displayStep = DisplayStepOption.BEFORE_PAYMENT;
    }

    if (modalType === QuestionnaireModalType.PICKUP) {
        field.displayStep = DisplayStepOption.BEFORE_PAYMENT;
    }

    return field;
};
